import { Graph } from '@vromo/api';
import { useStore } from '@vromo/mobx-react/mobx-react';
import { Vehicle } from '@vromo/models';
import { COURIER_DETAILS } from '@vromo/models/CourierInfo';
import TrackingLinkStore from '@vromo/tracking-link/TrackingLinkStore';
import Icon from '@vromo/ui/components/FontAwesome';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { qaLabelTrackingLink } from '../../../../../../../common-qa-labels/tracking-link';
import styles from './driverContact.style';
import { byMostRecent } from '@vromo/utils/arrayUtils';

interface Props {
    vehicle: Vehicle;
    host: string;
    auth: string;
}

const DriverContact = observer(({ vehicle, host, auth }: Props): React.ReactElement | null => {
    const { t } = useTranslation();
    const rootStore = useStore<TrackingLinkStore>('rootStore');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [usingCourierDetails, setUsingCourierDetails] = useState<boolean>(false);

    const job = rootStore.jobs.first()!;
    const courierDetails = job?.courierInfo
        ?.filter((item) => item.status === COURIER_DETAILS)
        .sort(byMostRecent);

    const courierPhoneNum = courierDetails?.length ? courierDetails[0]?.courierPhoneNumber : '';

    useEffect(() => {
        if (!vehicle || usingCourierDetails || job.status === 'open') return;

        const contactDriver = async () => {
            try {
                const {
                    items: [contactMethod]
                } = await new Graph(host).root
                    .track(auth, job.id!)
                    .job()
                    .contact()
                    .channels()
                    .post({ items: [{ methods: ['phone'] }] });

                if (contactMethod?.phone) {
                    setPhoneNumber(contactMethod?.phone);
                } else if (courierPhoneNum) {
                    setPhoneNumber(courierPhoneNum);
                    setUsingCourierDetails(true);
                    return;
                }
            } catch (err) {
                console.error(err);
            }
        };

        contactDriver();
    }, [vehicle?.driver?.contact?.phone, courierPhoneNum, usingCourierDetails, job.status]);

    useEffect(() => {
        if (job.status === 'open') {
            setPhoneNumber('');
            setUsingCourierDetails(false);
        }
    }, [job.status]);

    if (!phoneNumber || job.status !== 'underway') return null;
    if (
        !usingCourierDetails &&
        !job.contact!.showMaskedPhone({ type: 'phone', masking: job.campaign?.link?.proxyEnabled })
    )
        return null;

    return (
        <div style={styles.container}>
            <div data-testid={qaLabelTrackingLink.contactDetailsDiv} style={styles.row}>
                <div style={styles.driverName}>{t('contactDriver')}</div>
                <div
                    onClick={() => window.open(`tel:${phoneNumber}`, '_blank')}
                    style={styles.callButton}>
                    <div data-testid={qaLabelTrackingLink.contactPhoneNumber} style={styles.button}>
                        <Icon icon={'phone'} color={'black'} style={styles.callIcon} />
                        {phoneNumber}
                    </div>
                </div>
            </div>
            <div style={styles.lineDivider} />
            <div style={styles.infoText}>{t('callingInfo')}</div>
        </div>
    );
});

export default DriverContact;
